/**
 * Champs personnalisés fiche produit — upload image vers le serveur Tumby.
 */
(function () {
  const TUMBY_UPLOAD_URL = 'https://tumby-upload-server-production.up.railway.app/upload';
  const TUMBY_MAX_IMAGE_BYTES = 8 * 1024 * 1024;

  /**
   * @param {string} url
   * @returns {string}
   */
  function tumbyNormalizeUploadUrl(url) {
    if (!url) return '';
    const trimmed = String(url).trim();
    if (!trimmed) return '';
    if (trimmed.indexOf('http://') === 0 || trimmed.indexOf('https://') === 0) return trimmed;
    if (trimmed.indexOf('//') === 0) return 'https:' + trimmed;
    return 'https://' + trimmed.replace(/^\/+/, '');
  }

  /**
   * @param {string} dataUrl
   * @returns {Promise<string>}
   */
  async function tumbyUploadImageDataUrl(dataUrl) {
    const response = await fetch(TUMBY_UPLOAD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: dataUrl }),
    });

    if (!response.ok) {
      let detail = '';
      try {
        const text = await response.text();
        if (text) {
          try {
            const json = JSON.parse(text);
            detail = json.error || json.message || text.slice(0, 200);
          } catch (e) {
            detail = text.slice(0, 200);
          }
        }
      } catch (eRead) {}
      throw new Error(detail || 'Upload HTTP ' + response.status);
    }

    const json = await response.json();
    const url = tumbyNormalizeUploadUrl(json && json.url ? String(json.url) : '');
    if (!url) throw new Error('URL de fichier invalide après upload.');
    return url;
  }

  /**
   * @param {File} file
   * @returns {Promise<string>}
   */
  function fileToDataUrl(file) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();
      reader.onload = function () {
        resolve(typeof reader.result === 'string' ? reader.result : '');
      };
      reader.onerror = function () {
        reject(reader.error || new Error('Lecture du fichier impossible.'));
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * @param {HTMLElement} block
   * @param {string} message
   */
  function tumbySetImageStatus(block, message) {
    const status = block.querySelector('[data-tumby-line-item-upload-status]');
    if (status) status.textContent = message || '';
  }

  /**
   * @param {HTMLElement} block
   * @returns {HTMLInputElement | null}
   */
  function tumbyGetUrlInput(block) {
    const el = block.querySelector('[data-tumby-line-item-image-url]');
    return el instanceof HTMLInputElement ? el : null;
  }

  /**
   * @param {HTMLElement} block
   * @returns {HTMLInputElement | null}
   */
  function tumbyGetFileInput(block) {
    const el = block.querySelector('[data-tumby-line-item-file]');
    return el instanceof HTMLInputElement ? el : null;
  }

  /**
   * @param {HTMLElement} block
   * @returns {HTMLInputElement | null}
   */
  function tumbyGetFilenameInput(block) {
    const el = block.querySelector('[data-tumby-line-item-filename]');
    return el instanceof HTMLInputElement ? el : null;
  }

  /**
   * @param {HTMLElement} block
   * @returns {Promise<boolean>}
   */
  async function tumbyUploadImageBlock(block) {
    const urlInput = tumbyGetUrlInput(block);
    const fileInput = tumbyGetFileInput(block);
    const filenameInput = tumbyGetFilenameInput(block);
    if (!urlInput || !fileInput) return true;

    if (urlInput.value.trim()) return true;

    const file = fileInput.files && fileInput.files[0];
    if (!file) return true;

    if (!file.type || file.type.indexOf('image/') !== 0) {
      tumbySetImageStatus(block, 'Veuillez choisir un fichier image (JPG, PNG, etc.).');
      block.classList.add('tumby-line-item-fields__field--invalid');
      return false;
    }

    if (file.size > TUMBY_MAX_IMAGE_BYTES) {
      tumbySetImageStatus(block, 'Image trop volumineuse (max. 8 Mo).');
      block.classList.add('tumby-line-item-fields__field--invalid');
      return false;
    }

    if (filenameInput) filenameInput.value = file.name;

    block.classList.add('tumby-line-item-fields__field--uploading');
    tumbySetImageStatus(block, 'Envoi de votre photo en cours…');

    try {
      const dataUrl = await fileToDataUrl(file);
      const url = await tumbyUploadImageDataUrl(dataUrl);
      urlInput.value = url;
      fileInput.removeAttribute('required');
      tumbySetImageStatus(block, 'Photo enregistrée : ' + file.name);
      block.classList.remove('tumby-line-item-fields__field--invalid');
      return true;
    } catch (err) {
      const msg =
        err && err.message ? String(err.message) : 'Impossible d’envoyer la photo. Réessayez dans un instant.';
      tumbySetImageStatus(block, msg);
      block.classList.add('tumby-line-item-fields__field--invalid');
      return false;
    } finally {
      block.classList.remove('tumby-line-item-fields__field--uploading');
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  function tumbyValidateLineItemFields(form) {
    let isValid = true;
    /** @type {HTMLElement | null} */
    let firstInvalid = null;

    const textFields = form.querySelectorAll(
      '[data-tumby-line-item-field]:not([data-tumby-line-item-image-url]):not([data-tumby-line-item-filename])'
    );
    for (const field of textFields) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
      const value = field.value.trim();
      const fieldValid = !field.required || value.length > 0;
      field.classList.toggle('tumby-line-item-fields__input--invalid', !fieldValid);
      if (!fieldValid) {
        isValid = false;
        if (!firstInvalid) firstInvalid = field;
      }
    }

    const imageBlocks = form.querySelectorAll('[data-tumby-line-item-image-field]');
    for (const block of imageBlocks) {
      if (!(block instanceof HTMLElement)) continue;
      const urlInput = tumbyGetUrlInput(block);
      const fileInput = tumbyGetFileInput(block);
      const required =
        (urlInput && urlInput.hasAttribute('data-tumby-required')) ||
        (fileInput && fileInput.required);
      const hasUrl = urlInput && urlInput.value.trim().length > 0;
      const hasFile = fileInput && fileInput.files && fileInput.files.length > 0;
      const fieldValid = !required || hasUrl || hasFile;
      block.classList.toggle('tumby-line-item-fields__field--invalid', !fieldValid);
      if (!fieldValid) {
        isValid = false;
        if (!firstInvalid && fileInput) firstInvalid = fileInput;
      }
    }

    if (!isValid && firstInvalid) firstInvalid.focus({ preventScroll: false });
    return isValid;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {Promise<boolean>}
   */
  async function tumbyPrepareLineItemFields(form) {
    if (!tumbyValidateLineItemFields(form)) return false;

    const imageBlocks = form.querySelectorAll('[data-tumby-line-item-image-field]');
    for (const block of imageBlocks) {
      if (!(block instanceof HTMLElement)) continue;
      const ok = await tumbyUploadImageBlock(block);
      if (!ok) return false;
    }

    return true;
  }

  /**
   * @param {HTMLInputElement} fileInput
   */
  function tumbyBindImagePreview(fileInput) {
    const block = fileInput.closest('[data-tumby-line-item-image-field]');
    if (!block) return;

    const preview = block.querySelector('[data-tumby-line-item-image-preview]');
    const previewImg = block.querySelector('[data-tumby-line-item-image-preview-img]');
    const urlInput = tumbyGetUrlInput(block);
    const filenameInput = tumbyGetFilenameInput(block);

    fileInput.addEventListener('change', function () {
      if (urlInput) urlInput.value = '';
      const file = fileInput.files && fileInput.files[0];
      if (!file || !(preview instanceof HTMLElement) || !(previewImg instanceof HTMLImageElement)) {
        if (preview instanceof HTMLElement) preview.hidden = true;
        if (filenameInput) filenameInput.value = '';
        tumbySetImageStatus(block, '');
        return;
      }

      if (filenameInput) filenameInput.value = file.name;

      const objectUrl = URL.createObjectURL(file);
      previewImg.src = objectUrl;
      preview.hidden = false;
      tumbySetImageStatus(block, file.name + ' — envoi en cours…');
      previewImg.onload = function () {
        URL.revokeObjectURL(objectUrl);
      };

      void tumbyUploadImageBlock(block);
    });
  }

  function tumbyInitLineItemFields(root) {
    const scope = root || document;
    const fileInputs = scope.querySelectorAll('[data-tumby-line-item-file]');
    for (const input of fileInputs) {
      if (input instanceof HTMLInputElement) tumbyBindImagePreview(input);
    }
  }

  window.tumbyValidateLineItemFields = tumbyValidateLineItemFields;
  window.tumbyPrepareLineItemFields = tumbyPrepareLineItemFields;
  window.tumbyInitLineItemFields = tumbyInitLineItemFields;
  window.tumbyNormalizeUploadUrl = tumbyNormalizeUploadUrl;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      tumbyInitLineItemFields(document);
    });
  } else {
    tumbyInitLineItemFields(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) tumbyInitLineItemFields(event.target);
  });
})();
